import {
  Children,
  DetailedHTMLProps,
  FC,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import classNames from 'classnames';

import Button from '../Button';

import './TableRow.scss';

import { TestMetadata } from '../types';

export interface Props
  extends DetailedHTMLProps<
    HTMLAttributes<HTMLTableRowElement>,
    HTMLTableRowElement
  > {
  /** Row content */
  children?: ReactNode;
  /** Row classname */
  className?: string;
  /** Row accordion */
  accordion?: ReactNode;
  /** Rows that have the same number of cells and will appear inline */
  nestedRows?: ReactNode;
  /** Row accordion is default collapsed */
  defaultCollapsed?: boolean;
  /** Row is selected */
  isSelected?: boolean;
  /** Accordion is disabled */
  isAccordionDisabled?: boolean;
  /** Toggle expandable row */
  onToggle?: () => void;
  // Test IDs for component sections
  testMetadata?: TestMetadata;
}

const TableRow: FC<Props> = ({
  children,
  className,
  accordion,
  nestedRows,
  defaultCollapsed = true,
  isSelected,
  isAccordionDisabled,
  onToggle,
  testMetadata,
  ...otherProps
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  useEffect(() => {
    setCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  const handleClick = useCallback(() => {
    setCollapsed(!collapsed);
    if (onToggle) {
      onToggle();
    }
  }, [collapsed, onToggle]);

  const expandableRow = accordion || nestedRows;

  const tableWrapperClassNames = useMemo(
    () =>
      classNames(
        'sb-table-row',
        {
          ['sb-table-row-expanded']: expandableRow && !collapsed,
          ['sb-table-row-selected']: isSelected,
        },
        className,
      ),
    [expandableRow, collapsed, isSelected, className],
  );

  return (
    <>
      <tr {...otherProps} className={tableWrapperClassNames} {...testMetadata}>
        {expandableRow && (
          <td className="sb-table-cell sb-table-collapsible">
            <Button
              onClick={handleClick}
              styleType="demoted"
              sizeType="smaller"
              className="sb-table-collapsible-icon"
              iconOnly
              iconType={`fas fa-${
                collapsed ? 'chevron-right' : 'chevron-down'
              }`}
              {...(accordion && { disabled: isAccordionDisabled })}
            />
          </td>
        )}
        {children}
      </tr>
      {accordion && !collapsed && (
        <tr>
          <td
            className="sb-table-cell"
            style={{ padding: 0 }}
            colSpan={Children.count(children) + 1}
          >
            {accordion}
          </td>
        </tr>
      )}
      {nestedRows && !collapsed && nestedRows}
    </>
  );
};

export default TableRow;
